import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import queryString from 'query-string';
import { ListBullets, MapTrifold } from '@phosphor-icons/react';
import { SpotsRequest, getSpotsAction } from 'actions/actions';
import FindParkingSearch from 'components/Spots/FindParkingSearch';
import Results from 'components/Spots/Results';
import SpotMap, { MapView } from 'components/Spots/SpotMap';
import { Spot } from 'utils/constants';
import { LatLng, spotLatLng } from 'utils/spotSearch';
import { ResolvedPlace, zoomForPlace } from 'utils/geocoding';
import styles from 'components/Spots/FindParking.module.css';

// Each search fetches the 10 nearest spots. "Search Here" adds a batch to the list, which keeps the
// two most recent batches (newest first) so exploring the map never shows more than 20 at once.
const FETCH_LIMIT = 10;
const MAX_SHOWN = 20;
const DEFAULT_ZOOM = 10;
const LOCATE_ZOOM = 13;
const CARD_SELECT_ZOOM = 14;
// Phoenix, AZ — used when the URL has no coordinates.
const DEFAULT_CENTER: LatLng = { lat: 33.512258, lng: -112.072086 };

// Unique across mounts, so a response from a previous visit can never match a new request.
let nextSpotsRequestId = 1;

type Props = RouteComponentProps & {
    spots: Spot[];
    loading: boolean;
    /** Which request the current `spots` answer; null before the first fetch completes. */
    lastRequest: SpotsRequest | null;
    getSpots: (coords: number[], rentalType: string, limit?: number, request?: SpotsRequest) => void;
};

const firstParam = (value: string | string[] | null | undefined) => (Array.isArray(value) ? value[0] : value);

const SpotsModuleContainer: React.FC<Props> = ({ location, history, spots, loading, lastRequest, getSpots }) => {
    const params = queryString.parse(location.search);
    const urlLat = firstParam(params.lat);
    const urlLng = firstParam(params.lng);
    const rentalType = firstParam(params.rentalType) || 'hourly';

    // The view the app asks the map to show. User pans and zooms never write back here,
    // so re-renders can't drag the map away from where the user left it.
    const [view, setView] = useState<MapView>({ center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM });
    const [origin, setOrigin] = useState<LatLng | undefined>(undefined);
    // What the list and map show: the latest fetch, plus the previous batch when exploring.
    const [shownSpots, setShownSpots] = useState<Spot[]>([]);
    // The most recent request we issued. Responses to anything else are stale and ignored, so an
    // overlapping search and "Search Here" can't apply each other's mode or results.
    const latestRequestIdRef = useRef(0);
    const lastBatchSizeRef = useRef(0);
    const [hoveredPk, setHoveredPk] = useState<Spot['pk'] | null>(null);
    const [pinnedPk, setPinnedPk] = useState<Spot['pk'] | null>(null);
    const [showPointsOfInterest, setShowPointsOfInterest] = useState(true);
    // Where the last address/venue search landed (URL coordinates or a search-bar pick).
    const [searchMarker, setSearchMarker] = useState<LatLng | null>(null);
    // Where "Use my location" put the user.
    const [userLocation, setUserLocation] = useState<LatLng | null>(null);
    // Narrow screens show one pane at a time; wide screens ignore this and show both.
    const [mobileView, setMobileView] = useState<'list' | 'map'>('list');
    const listRef = useRef<HTMLDivElement>(null);
    const sidebarRef = useRef<HTMLElement>(null);
    // The results panel floats over the map, so the camera is padded past it on wide screens.
    const [mapPaddingLeft, setMapPaddingLeft] = useState(0);

    const search = useCallback(
        (coords: LatLng, options: { append?: boolean } = {}) => {
            const request: SpotsRequest = { id: nextSpotsRequestId++, append: Boolean(options.append) };
            latestRequestIdRef.current = request.id;
            setOrigin(coords);
            getSpots([coords.lat, coords.lng], rentalType, FETCH_LIMIT, request);
        },
        [getSpots, rentalType]
    );

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    useEffect(() => {
        const start = urlLat && urlLng ? { lat: +urlLat, lng: +urlLng } : DEFAULT_CENTER;
        setView((current) => ({ center: start, zoom: current.zoom }));
        setSearchMarker(urlLat && urlLng ? start : null);
        search(start);
    }, [search, urlLat, urlLng]);

    // Apply the response to our latest request: replace the list, or put the new batch ahead of the
    // previous one. Responses to earlier requests (or from a previous visit) are ignored.
    useEffect(() => {
        if (!lastRequest || lastRequest.id !== latestRequestIdRef.current) return;
        lastBatchSizeRef.current = spots.length;
        setShownSpots((current) => {
            if (!lastRequest.append) return spots;
            const fresh = new Set(spots.map((spot) => spot.pk));
            return [...spots, ...current.filter((spot) => !fresh.has(spot.pk))].slice(0, MAX_SHOWN);
        });
        setPinnedPk(null);
        if (listRef.current) listRef.current.scrollTop = 0;
    }, [spots, lastRequest]);

    // Still fetching until the response to the latest request has landed.
    const isFetching = loading || !lastRequest || lastRequest.id !== latestRequestIdRef.current;

    useEffect(() => {
        const measure = () => {
            const panel = sidebarRef.current;
            setMapPaddingLeft(panel && window.innerWidth > 900 ? panel.getBoundingClientRect().right : 0);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    const activeSpotPk = hoveredPk ?? pinnedPk;
    // A full batch means more spots likely exist beyond the ones shown; "Search Here" adds them.
    const shown = shownSpots.length;
    const resultsLabel =
        lastBatchSizeRef.current >= FETCH_LIMIT
            ? `Showing ${shown}, more locations nearby`
            : `Showing ${shown} ${shown === 1 ? 'location' : 'locations'} nearby`;

    const handleSearchHere = (mapCenter: LatLng) => {
        setSearchMarker(null);
        search(mapCenter, { append: true });
    };

    const handleLocate = (next: LatLng) => {
        setUserLocation(next);
        setSearchMarker(null);
        setView((current) => ({ center: next, zoom: Math.max(current.zoom, LOCATE_ZOOM) }));
        search(next);
    };

    const handlePlaceSelect = (place: ResolvedPlace) => {
        setView({ center: place.center, zoom: zoomForPlace(place.placeTypes) });
        history.push(`/spots?lat=${place.center.lat}&lng=${place.center.lng}&rentalType=${rentalType}`);
    };

    // Tapping a card flies the map to its pin and opens the popup.
    const handleCardSelect = (spot: Spot) => {
        setPinnedPk(spot.pk);
        setMobileView('map');
        setView((current) => ({ center: spotLatLng(spot), zoom: Math.max(current.zoom, CARD_SELECT_ZOOM) }));
    };

    const handlePinClick = (spotPk: Spot['pk']) => {
        setPinnedPk((current) => (current === spotPk ? null : spotPk));
    };

    return (
        <div className={styles.page}>
            <FindParkingSearch proximity={origin || view.center} onSelect={handlePlaceSelect} />
            <div className={`${styles.grid} ${mobileView === 'map' ? styles.gridMap : styles.gridList}`}>
                <aside className={styles.sidebar} ref={sidebarRef}>
                    <div className={styles.sidebarHeader}>
                        <span className={styles.pageLabel}>{resultsLabel}</span>
                    </div>
                    <div className={styles.list} ref={listRef}>
                        <Results
                            spots={shownSpots}
                            rentalType={rentalType}
                            origin={origin}
                            activeSpotPk={activeSpotPk}
                            selectedSpotPk={pinnedPk}
                            loading={isFetching}
                            onHover={setHoveredPk}
                            onSelect={handleCardSelect}
                        />
                    </div>
                </aside>

                <SpotMap
                    spots={shownSpots}
                    view={view}
                    paddingLeft={mapPaddingLeft}
                    origin={origin}
                    searchMarker={searchMarker}
                    userLocation={userLocation}
                    rentalType={rentalType}
                    activeSpotPk={activeSpotPk}
                    loading={isFetching}
                    showPointsOfInterest={showPointsOfInterest}
                    onTogglePointsOfInterest={() => setShowPointsOfInterest((current) => !current)}
                    onHover={setHoveredPk}
                    onPinClick={handlePinClick}
                    onMapClick={() => setPinnedPk(null)}
                    onSearchHere={handleSearchHere}
                    onLocate={handleLocate}
                    layoutKey={mobileView}
                />

                <div className={styles.mobileViewToggle} role="group" aria-label="Show list or map">
                    <button
                        type="button"
                        className={`${styles.mobileViewButton} ${
                            mobileView === 'list' ? styles.mobileViewButtonActive : ''
                        }`}
                        aria-pressed={mobileView === 'list'}
                        onClick={() => setMobileView('list')}
                    >
                        <ListBullets size={16} weight="bold" />
                        List
                    </button>
                    <button
                        type="button"
                        className={`${styles.mobileViewButton} ${
                            mobileView === 'map' ? styles.mobileViewButtonActive : ''
                        }`}
                        aria-pressed={mobileView === 'map'}
                        onClick={() => setMobileView('map')}
                    >
                        <MapTrifold size={16} weight="bold" />
                        Map
                    </button>
                </div>
            </div>
        </div>
    );
};

function mapStateToProps(state: any) {
    return {
        loading: state.spots.loading,
        spots: state.spots.spots,
        lastRequest: state.spots.lastRequest || null,
    };
}

const mapDispatchToProps = {
    getSpots: getSpotsAction,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SpotsModuleContainer));
